/***********************************************
ICEMEDX - Developed By ICEMEDX, Inc.
Copyright 2018 ICEMEDX, Inc.
***********************************************/
import { IceNavService } from '../../../../projects/icemedx-lib/src/components/ice-nav/ice-nav.service';
import { of, Subject, Subscription, throwError } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';

import { IceTypes } from 'projects/icemedx-lib/src/models/icetypes';
import { Iceutility } from 'projects/icemedx-lib/src/services/iceutility';
import { patient } from 'projects/icemedx-lib/src/models/domain/patient';
import { DomainService } from 'projects/icemedx-lib/src/services/domain/domain.service';
import { filter } from 'rxjs/internal/operators/filter';
import { patientindex } from '../models/domain/patientindex';
import { userTypes } from '../login/login.config';
import {
  IcecontrolPermission,
  IcecontrolSettings,
  IcepanelsectionSettings,
  SECTION_CONTROL_ALIGNMENT
} from '../components/models/icesettings';
import { provider_index } from '../models/domain/provider_index';
import { LoginService } from '../login/login.service';
import { Router } from '@angular/router';
import { basecode } from '../models/ehr/subs/basecode';
import { MedicalcodesService } from './medicalcodes/medicalcodes.service';
import { payer_index } from '../models/domain/payer_index';
import { subscriber } from '../models/domain/subscriber';
import { ApplicationRef } from '@angular/core';
import { IceNavComponent } from '../components/ice-nav/ice-nav.component';
import { MatTableDataSource } from '@angular/material';

const FARM = "FARM";
const YARD = "YARD";
const TREE = "TREE";
const BRANCH = "BRANCH";

export class BaseAppService {

  // Data Members
  private currentPatient: patient = null;
  private currentSubscriber: subscriber = null;
  private breadCrumbs: any = {
    FARM: { display: "", data: null, active: false },
    YARD: { display: "", data: null, active: false },
    TREE: { display: "", data: null, active: false },
    BRANCH: { display: "", data: null, active: false },
    LEAVES: []
  };
  private patientReadySbj = new Subject<patient>();
  private subscriberReadySbj = new Subject<subscriber>();
  private showPrivateNetworkToolbar: boolean = false;

  // General Panel editing
  editPanelSectionMap: any = {};
  editPanelSectionRowData: any = {};

  stateOptions: any[] = [];
  countryOptions: any[] = [];

  constructor(private navSvc: IceNavService, private domainSvc: DomainService, private loginSvc: LoginService, private app: ApplicationRef) {
  }

  public defaultMenus() {
    this.navSvc.setAllMessages("messages/inbox");

    let curAct: string = this.getCurrentAccount();
    let curPtAct: string = this.getCurrentPatient();
    let curSubAct: string = this.getCurrentSubscriber();
    let curActType: IceTypes.ACCOUNT_TYPE = this.getCurrentAccountType();
    this.breadCrumbs.FARM = { display: "", data: null, active: false, link: "" };
    this.breadCrumbs.YARD = { display: "", data: null, active: false, link: "" };
    this.breadCrumbs.TREE = { display: "", data: null, active: false, link: "" };
    this.breadCrumbs.BRANCH = { display: "", data: null, active: false, link: "" };
    this.breadCrumbs.LEAVES = [];
    this.breadCrumbs.TOOLBARS = [];

    this.refreshAccountCache(curAct, curActType);
    this.refreshPatientCache(curPtAct, curAct);
    this.refreshSubscriberCache(curSubAct, curAct);

    if (this.showPrivateNetworkToolbar) {
      let privateNetworkBar = this.addBreadcrumbToolbar("PrivateNetworkToolbar");
      this.domainSvc.getPatients().subscribe(
        data => {
          let pts = data;
          if (pts) {
            if (privateNetworkBar != null) {
              let menuItems: any[] = [
                { display: "", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufNone, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "root", link: "patientdashboard" },
                { display: "Patient Dashboard", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufNone, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "dashboard", link: "patientdashboard" },
                { display: "Contact Information", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufDemographics, userTypes.USER_FEATURE_ACCESS.ufaFullControl), active: true, id: "contact", link: "patientportal/contact" }
              ];

              for (let ipt = 0; ipt < pts.length; ipt++) {
                let ptIdx: patientindex = <patientindex>pts[ipt];
                privateNetworkBar.items.push({
                  display: " (" + Iceutility.abbreviate(ptIdx.getdisplayname()) + ")",
                  menuitem: "appMenu1",
                  data: ptIdx,
                  domain: ptIdx.trans_account,
                  domaintype: IceTypes.ACCOUNT_TYPE.actPatient,
                  link: "", properties: {
                    class: { "fa": true, "fa-id-card-alt": true, "fa-3x": true },
                    color: ptIdx.trans_account == curAct ? 'gray' : 'green'
                  }
                });
              }
              privateNetworkBar["appMenu1"] = { menuItems: menuItems };
            }
          }
        },
        error => {
          console.log(JSON.stringify(this.domainSvc.getLastErrors()));
        });

      this.domainSvc.getProviders().subscribe(
        data => {
          let prvs = data;
          if (prvs) {
            if (privateNetworkBar != null) {
              let menuItems: any[] = [
                { display: "", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufNone, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "root", link: "", externallink: "EHR/Schedule?vw=dayView&date=&todate=" },
                { display: "Dashboard", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufSchedule, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "dashboard", link: "dashboard", externallink: "" },
                { display: "Patient Schedule", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufSchedule, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "schedule", link: "", externallink: "EHR/Schedule?vw=dayView&date=&todate=" },
                { display: "Encounter Status", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufEncounters, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "encounters", link: "", externallink: "EHR/Status" },
                { display: "Network Profile", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufSetup, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "domainprofile", link: "domainprofile/provider", externallink: "" },
                { display: "Accounting", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufAccountingJournals, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "accounting", link: "accounting/summary", externallink: "" },
                { display: "Inventory", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufOtherItemsSoldandServices, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "accounting", link: "accounting/inventory", externallink: "" }
              ];

              for (let ipro = 0; ipro < prvs.length; ipro++) {
                let proIdx: provider_index = <provider_index>prvs[ipro];
                if (proIdx == null) continue;
                proIdx.account_type = Iceutility.toLong(proIdx.account_type);
                privateNetworkBar.items.push({
                  display: " (" + Iceutility.abbreviate(proIdx.getdisplayname(proIdx.account_type === IceTypes.ACCOUNT_TYPE.actFacility)) + ")",
                  menuitem: "appMenu2",
                  data: proIdx,
                  domain: proIdx.trans_account,
                  domaintype: Iceutility.toLong(proIdx.account_type),
                  link: "", properties: {
                    class: proIdx.account_type === IceTypes.ACCOUNT_TYPE.actFacility ?
                      { "fas": true, "fa-hospital": true, "fa-3x": true } : { "fas": true, "fa-user-md": true, "fa-3x": true },
                    color: proIdx.trans_account == curAct ? 'gray' : 'blue'
                  }
                });
              }
              privateNetworkBar["appMenu2"] = { menuItems: menuItems };
            }
          }
        },
        error => {
          console.log(JSON.stringify(this.domainSvc.getLastErrors()));
        });

      this.domainSvc.getPharmacies().subscribe(
        data => {
          let prys = data;
          if (prys) {
            if (privateNetworkBar != null) {
              let menuItems: any[] = [
                { display: "", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufNone, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "root", link: "pharmacy/patientsearch", externallink: "" },
                { display: "Dashboard", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufSchedule, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "dashboard", link: "dashboard", externallink: "" },
                { display: "Network Profile", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufSetup, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "domainprofile", link: "domainprofile/pharmacy", externallink: "" },
                { display: "Patient Search", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufDemographics, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "patientsearch", link: "pharmacy/patientsearch", externallink: "" },
                { display: "Meetings", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufSchedule, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "meetings", link: "pharmacy/meetings", externallink: "" },
                { display: "Orders", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufOrders, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "orders", link: "pharmacy/documents", externallink: "" },
                { display: "Accounting", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufAccountingJournals, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "accounting", link: "accounting/summary", externallink: "" },
              ];

              for (let ipyr = 0; ipyr < prys.length; ipyr++) {
                let pryIdx: payer_index = <payer_index>prys[ipyr];
                if (pryIdx == null) continue;
                privateNetworkBar.items.push({
                  display: " (" + Iceutility.abbreviate(pryIdx.getdisplayname(true)) + ")",
                  menuitem: "appMenu5",
                  data: pryIdx,
                  domain: pryIdx.trans_account,
                  domaintype: IceTypes.ACCOUNT_TYPE.actPharmacy,
                  link: "", properties: {
                    class: { "fas": true, "fa-building": true, "fa-3x": true },
                    color: pryIdx.trans_account == curAct ? 'gray' : 'red'
                  }
                });
              }
              privateNetworkBar["appMenu5"] = { menuItems: menuItems };
            }
          }
        },
        error => {
          console.log(JSON.stringify(this.domainSvc.getLastErrors()));
        });

      this.domainSvc.getPayers().subscribe(
        data => {
          let prys = data;
          if (prys) {
            if (privateNetworkBar != null) {
              let menuItems: any[] = [
                { display: "", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufNone, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "root", link: "domainprofile/payer", externallink: "" },
                { display: "Dashboard", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufSchedule, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "dashboard", link: "dashboard", externallink: "" },
                { display: "Network Profile", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufSetup, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "domainprofile", link: "domainprofile/payer", externallink: "" },
                { display: "Subscriber Search", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufDemographics, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "subscribersearch", link: "payer/subscribersearch", externallink: "" },
                { display: "Meetings", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufSchedule, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "meetings", link: "payer/meetings", externallink: "" },
                { display: "Documents", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufExternalDocuments, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "documents", link: "payer/documents", externallink: "" },
                { display: "Accounting", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufAccountingJournals, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "accounting", link: "accounting/summary", externallink: "" },
              ];

              for (let ipyr = 0; ipyr < prys.length; ipyr++) {
                let pryIdx: payer_index = <payer_index>prys[ipyr];
                if (pryIdx == null) continue;
                privateNetworkBar.items.push({
                  display: " (" + Iceutility.abbreviate(pryIdx.getdisplayname(true)) + ")",
                  menuitem: "appMenu3",
                  data: pryIdx,
                  domain: pryIdx.trans_account,
                  domaintype: IceTypes.ACCOUNT_TYPE.actPayer,
                  link: "", properties: {
                    class: { "fas": true, "fa-hospital-alt": true, "fa-3x": true },
                    color: pryIdx.trans_account == curAct ? 'gray' : 'orange'
                  }
                });
              }
              privateNetworkBar["appMenu3"] = { menuItems: menuItems };
            }
          }
        },
        error => {
          console.log(JSON.stringify(this.domainSvc.getLastErrors()));
        });

      this.domainSvc.getSubscribers().subscribe(
        data => {
          let pts = data;
          if (pts) {
            if (privateNetworkBar != null) {
              let menuItems: any[] = [
                { display: "", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufNone, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "root", link: "subscriberdashboard" },
                { display: "Subscriber Dashboard", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufNone, userTypes.USER_FEATURE_ACCESS.ufaReadOnly), active: true, id: "dashboard", link: "subscriberdashboard" },
                { display: "Contact Information", permission: new IcecontrolPermission(userTypes.USER_FEATURES.ufDemographics, userTypes.USER_FEATURE_ACCESS.ufaFullControl), active: true, id: "contact", link: "subscriberportal/contact" }
              ];

              for (let ipt = 0; ipt < pts.length; ipt++) {
                let ptIdx: patientindex = <patientindex>pts[ipt];
                privateNetworkBar.items.push({
                  display: " (" + Iceutility.abbreviate(ptIdx.getdisplayname()) + ")",
                  menuitem: "appMenu4",
                  data: ptIdx,
                  domain: ptIdx.trans_account,
                  domaintype: IceTypes.ACCOUNT_TYPE.actPatient,
                  link: "", properties: {
                    class: { "fa": true, "fa-id-card-alt": true, "fa-3x": true },
                    color: ptIdx.trans_account == curAct ? 'gray' : 'purple'
                  }
                });
              }
              privateNetworkBar["appMenu4"] = { menuItems: menuItems };
            }
          }
        },
        error => {
          console.log(JSON.stringify(this.domainSvc.getLastErrors()));
        });

    }

  }

  public setCustomBrandName(name: string) {
    let navComp = this.app.bootstrap(IceNavComponent);

    let defBrand = 'NetworkNow EHR';
    if (this.loginSvc.whitelabel_brand != null && this.loginSvc.whitelabel_brand.length > 0) {
      defBrand = this.loginSvc.whitelabel_brand;
      if (defBrand.length > 30 && Iceutility.isMobileDevice()) {
        defBrand = defBrand.substring(0, 27) + "..."
      }
      if (navComp) navComp.instance.brandTrademark = false;
    }

    if (navComp) navComp.instance.brandName = name != null && name.length ? name : defBrand;
  }

  public networkMenu() {
    let loginSvc = this.getLoginService();
    let usrInfo = loginSvc.GetSessionUserInfo();
    let navSvc: IceNavService = this.getNavService();
    if (this.getMainAppChildMode() == false) {
      // add the commented code below later once the functionality is working
      navSvc.insertSideRoute(null, "dashboard", null, "dashboard", "Dashboard", "fas fa-tachometer-alt", 0);

      let ptact: string = this.getCurrentPatient();
      let domain: string = this.getCurrentAccount();

      if (loginSvc.hasDomainType(usrInfo, IceTypes.ACCOUNT_TYPE.actProvider)) {
        navSvc.insertSideRoute(null, "providers", null, "domain/" + IceTypes.ACCOUNT_TYPE.actProvider.toString(), "Providers", "fas fa-user-md", 0);
      }

      if (loginSvc.hasDomainType(usrInfo, IceTypes.ACCOUNT_TYPE.actFacility)) {
        navSvc.insertSideRoute(null, "hospitals", null, "domain/" + IceTypes.ACCOUNT_TYPE.actFacility.toString(), "Hospitals & Orgs.", "fas fa-hospital", 0);
      }

      if (loginSvc.hasDomainType(usrInfo, IceTypes.ACCOUNT_TYPE.actPharmacy)) {
        navSvc.insertSideRoute(null, "pharmacies", null, "domain/" + IceTypes.ACCOUNT_TYPE.actPharmacy.toString(), "Pharmacies", "fas fa-building", 0);
      }

      if (loginSvc.hasDomainType(usrInfo, IceTypes.ACCOUNT_TYPE.actPayer)) {
        navSvc.insertSideRoute(null, "payers", null, "domain/" + IceTypes.ACCOUNT_TYPE.actPayer.toString(), "Payer(s)", "fas fa-hospital-alt", 0);
      }

      if (loginSvc.hasDomainType(usrInfo, IceTypes.ACCOUNT_TYPE.actPatient)) {
        navSvc.insertSideRoute(null, "patients", null, "domain/" + IceTypes.ACCOUNT_TYPE.actPatient.toString(), "Patient Portal(s)", "fas fa-id-card-alt", 0);
      }

      if (loginSvc.hasDomainType(usrInfo, IceTypes.ACCOUNT_TYPE.actSubscriber)) {
        navSvc.insertSideRoute(null, "subscribers", null, "domain/" + IceTypes.ACCOUNT_TYPE.actSubscriber.toString(), "Subscriber Portal(s)", "fas fa-id-card", 0);
      }

      if (loginSvc.hasDomainType(usrInfo, IceTypes.ACCOUNT_TYPE.actVendor)) {
        navSvc.insertSideRoute(null, "vendors", null, "domain/" + IceTypes.ACCOUNT_TYPE.actVendor.toString(), "Vendors", "fas fa-handshake", 0);
      }
      //navSvc.insertSideRoute(null, "requestaccess", null, "requestaccess", "Request Access", "fas fa-search", 0);
      navSvc.insertSideRoute(null, "members", null, "members", "Public Network Members", "fas fa-users", 0);
    }
    else {
      navSvc.showTopMenuParent(navSvc.topMenuMessagesParentElementID, false);
      navSvc.showTopMenuParent(navSvc.topMenuLogoutParentElementID, false);
    }
  }

  public groupAdminMenus() {
    let navSvc: IceNavService = this.getNavService();
    let loginSvc = this.getLoginService();
    let usrInfo = loginSvc.GetSessionUserInfo();
    let domain = this.getCurrentAccount();
    let domType = this.getCurrentAccountType();
    let grp = loginSvc.getHighestGroup(usrInfo, domain);
    if (grp >= userTypes.USER_GROUPS.upAD) {
      let url = loginSvc.getPrivateNetworkUri(domain, domType, "", IceTypes.ACCOUNT_TYPE.actNone, true);
      navSvc.insertSideRoute(null, "privatenetwork", null, "privatenetwork", "Private Network Members", "fas fa-users-cog", 0, url);
    }
  }

  public homeMenu() {
    let navSvc: IceNavService = this.getNavService();
    let domType = this.getCurrentAccountType();
    if (domType == IceTypes.ACCOUNT_TYPE.actFacility) navSvc.insertSideRoute(null, "search", null, "provider/search", "Home", "fas fa-home", 0, "EHR/Schedule?vw=dayView&date=&todate=");
    else if (domType == IceTypes.ACCOUNT_TYPE.actProvider) navSvc.insertSideRoute(null, "search", null, "provider/search", "Home", "fas fa-home", 0, "EHR/Schedule?vw=dayView&date=&todate=");
    else if (domType == IceTypes.ACCOUNT_TYPE.actPharmacy) navSvc.insertSideRoute(null, "search", null, "pharmacy/search", "Home", "fas fa-home", 0);
    else if (domType == IceTypes.ACCOUNT_TYPE.actPayer) navSvc.insertSideRoute(null, "search", null, "payer/subscribersearch", "Home", "fas fa-home", 0);
  }

  public payerdomainMenus(): void {
    let navSvc: IceNavService = this.getNavService();
    let ptact: string = this.getCurrentPatient();
    let subact: string = this.getCurrentSubscriber();

    navSvc.insertSideRoute(null, "dashboard", null, "dashboard", "Dashboard", "fas fa-tachometer-alt", 0);
    navSvc.insertSideRoute(null, "profile", null, "domainprofile/payer", "Network Profile", "fas fa-cog", 0);
    navSvc.insertSideRoute(null, "addsubscriber", null, "payer/addsubscriber", "Add Subscriber", "fas fa-user-plus", 0);
    navSvc.insertSideRoute(null, "subscribersearch", null, "payer/subscribersearch", "Subscriber Search", "fas fa-search", 0);
    //navSvc.insertSideRoute(null, "patientsearch", null, "payer/patientsearch", "Patient Search", "fas fa-search-plus", 0);
    navSvc.insertSideRoute(null, "meetings", null, "payer/meetings", "Meetings", "fas fa-calendar-check", 0);
    navSvc.insertSideRoute(null, "documents", null, "payer/documents", "Documents", "fas fa-file-alt", 0);
    navSvc.insertSideRoute(null, "accounting", null, "accounting/summary", "Accounting", "fas fa-money-bill", 0);
    navSvc.insertSideRoute(null, "servicecharge", null, "accounting/serviceitemcharge/0", "Service Charges", "fas fa-shopping-cart", 0);
  }

  public pharmacydomainMenus(): void {
    let navSvc: IceNavService = this.getNavService();
    let ptact: string = this.getCurrentPatient();
    let subact: string = this.getCurrentSubscriber();

    navSvc.insertSideRoute(null, "dashboard", null, "dashboard", "Dashboard", "fas fa-tachometer-alt", 0);
    navSvc.insertSideRoute(null, "profile", null, "domainprofile/pharmacy", "Network Profile", "fas fa-cog", 0);
    navSvc.insertSideRoute(null, "addpatient", null, "pharmacy/addpatient", "Add Patient", "fas fa-user-plus", 0);
    navSvc.insertSideRoute(null, "patientsearch", null, "pharmacy/patientsearch", "Patient Search", "fas fa-search-plus", 0);
    navSvc.insertSideRoute(null, "meetings", null, "pharmacy/meetings", "Meetings", "fas fa-calendar-check", 0);
    navSvc.insertSideRoute(null, "documents", null, "pharmacy/documents", "Documents", "fas fa-file-alt", 0);
    navSvc.insertSideRoute(null, "accounting", null, "accounting/summary", "Accounting", "fas fa-money-bill", 0);
    navSvc.insertSideRoute(null, "servicecharge", null, "accounting/serviceitemcharge/0", "Service Charges", "fas fa-shopping-cart", 0);
  }

  public providerdomainMenus(): void {
    let navSvc: IceNavService = this.getNavService();
    let ptact: string = this.getCurrentPatient();
    let subact: string = this.getCurrentSubscriber();

    navSvc.insertSideRoute(null, "dashboard", null, "dashboard", "Dashboard", "fas fa-tachometer-alt", 0);
    navSvc.insertSideRoute(null, "accounting", null, "accounting/summary", "Accounting Summary", "fas fa-money-bill", 0);
    navSvc.insertSideRoute(null, "inventory", null, "accounting/inventory", "Inventory", "fas fa-cog", 0);
    navSvc.insertSideRoute(null, "encounterstatus", null, "EHR/encounterstatus", "Encounter Status", "fas fa-briefcase", 0, "EHR/Status");
    navSvc.insertSideRoute(null, "orders", null, "EHR/Orders", "Order List", "fas fa-medkit", 0, "EHR/Orders");
    navSvc.insertSideRoute(null, "patientsearch", null, "EHR/search", "Patient Search", "fas fa-search-plus", 0, "EHR/Search?key=");
  }

  public providerPatientdomainMenus(): void {
    let navSvc: IceNavService = this.getNavService();
    let ptact: string = this.getCurrentPatient();

    navSvc.insertSideRoute(null, "dashboard", null, "dashboard", "Dashboard", "fas fa-tachometer-alt", 0);
    navSvc.insertSideRoute(null, "profile", null, "domainprofile/provider", "Network Profile", "fas fa-cog", 0);
    navSvc.insertSideRoute(null, "patientschedule", null, "provider/schedule", "Patient Schedule", "fas fa-calendar-alt", 0, "EHR/Schedule?vw=dayView&date=&todate=");
    navSvc.insertSideRoute(null, "encounterstatus", null, "provider/encounterstatus", "Encounter Status", "fas fa-briefcase", 0, "EHR/Status");
    navSvc.insertSideRoute(null, "reports", null, "reports", "Reports", "fas fa-chart-pie", 0);
    navSvc.insertSideRoute(null, "orders", null, "provider/orders", "Orders", "fas fa-medkit", 0, "EHR/Orders");
    navSvc.insertSideRoute(null, "accounting", null, "accounting/summary", "Accounting", "fas fa-money-bill", 0);
    navSvc.insertSideRoute(null, "servicecharge", null, "accounting/serviceitemcharge/0", "Service Charges", "fas fa-shopping-cart", 0);
  }

  public refreshMenus() {
    this.navSvc.refresh();
  }

  public getNavService(): IceNavService {
    return this.navSvc;
  }

  public getLoginService(): LoginService {
    return this.loginSvc;
  }

  public getBreadcrumbs(): any {
    return this.breadCrumbs;
  }

  public getBaseRoutePath(routeName: string): string {
    return "";
  }

  public getCurrentAccount(): string {
    return sessionStorage.getItem("g_currentact");
  }

  public getCurrentAccountType(): IceTypes.ACCOUNT_TYPE {
    return <IceTypes.ACCOUNT_TYPE>Iceutility.toLong(sessionStorage.getItem("g_currentacttype"));
  }

  public getCurrentPatient(): string {
    return sessionStorage.getItem("g_currentpatient");
  }

  public getCurrentSubscriber(): string {
    return sessionStorage.getItem("g_currentsubscriber");
  }

  public openAccount(act: string, acttype: IceTypes.ACCOUNT_TYPE, setFormat: boolean = true) {
    sessionStorage.setItem("g_currentact", act);
    sessionStorage.setItem("g_currentacttype", acttype.toString());
    sessionStorage.removeItem("g_currentdateformat");

    this.refreshAccountCache(act, acttype, setFormat);
  }

  public openPatient(ptAct: string, makePrimaryAct: boolean): void {
    if (makePrimaryAct) {
      sessionStorage.setItem("g_currentact", ptAct);
      sessionStorage.setItem("g_currentacttype", IceTypes.ACCOUNT_TYPE.actPatient.toString());
    }
    sessionStorage.setItem("g_currentpatient", ptAct);
    let curAct = this.getCurrentAccount();
    this.refreshPatientCache(ptAct, curAct);
  }

  public openSubscriber(subAct: string, makePrimaryAct: boolean): void {
    if (makePrimaryAct) {
      sessionStorage.setItem("g_currentact", subAct);
      sessionStorage.setItem("g_currentacttype", IceTypes.ACCOUNT_TYPE.actSubscriber.toString());
    }
    sessionStorage.setItem("g_currentsubscriber", subAct);
    let curAct = this.getCurrentAccount();
    this.refreshSubscriberCache(subAct, curAct);
  }

  public closeAccount(): void {
    sessionStorage.setItem("g_currentact", "");
    sessionStorage.setItem("g_currentacttype", "");
  }

  public closePatient(): void {
    sessionStorage.setItem("g_currentpatient", "");
  }

  public closeSubscriber(): void {
    sessionStorage.setItem("g_currentsubscriber", "");
  }

  public getMainAppChildMode(): boolean {
    return sessionStorage.getItem("mainappChildMode") == "1";
  }

  public refreshPatientCache(curPtAct: string, curAct: string) {
    if (curPtAct == null || curPtAct.length == 0) {
      return;
    }

    if (curPtAct == curAct) {
      return;
    }

    this.currentPatient = null;
    this.domainSvc.getPatient(curPtAct, curAct).subscribe(
      data => {
        if (data != null && data.length > 0) this.currentPatient = <patient>data[0];

        if (this.getCurrentAccountType() == IceTypes.ACCOUNT_TYPE.actPatient) {
          this.breadCrumbs.FARM.data = data != null && data.length > 0 ? data[0] : null;
          if (this.currentPatient != null) {
            this.breadCrumbs.FARM.display = this.getPatientName(this.currentPatient) + " Chart No. #" + this.currentPatient.trans_account;
          }
        }
        else {
          this.breadCrumbs.YARD.data = data != null && data.length > 0 ? data[0] : null;
          if (this.currentPatient != null) {
            this.breadCrumbs.YARD.display = this.getPatientName(this.currentPatient) + " Chart No. #" + this.currentPatient.trans_account;
            if (!this.getMainAppChildMode())
              this.breadCrumbs.YARD.link = this.navSvc.buildRoute("PHR", true);
          }
        }
        this.patientReadySbj.next(this.currentPatient);
      },
      error => {
        console.log(JSON.stringify(this.domainSvc.getLastErrors()));
      });
  }

  public refreshSubscriberCache(curSubAct: string, curAct: string) {
    if (curSubAct == null || curSubAct.length == 0) {
      return;
    }

    if (curSubAct == curAct) {
      return;
    }

    this.currentSubscriber = null;
    this.domainSvc.getSubscriber(curSubAct, curAct).subscribe(
      data => {
        if (data != null && data.length > 0) this.currentSubscriber = <subscriber>data[0];

        if (this.getCurrentAccountType() == IceTypes.ACCOUNT_TYPE.actSubscriber) {
          this.breadCrumbs.FARM.data = data != null && data.length > 0 ? data[0] : null;
          if (this.currentSubscriber != null) {
            this.breadCrumbs.FARM.display = this.getSubscriberName(this.currentSubscriber) + " Member #" + this.currentPatient.trans_account;
          }
        }
        else {
          this.breadCrumbs.YARD.data = data != null && data.length > 0 ? data[0] : null;
          if (this.currentSubscriber != null) {
            this.breadCrumbs.YARD.display = this.getSubscriberName(this.currentSubscriber) + " Member #" + this.currentPatient.trans_account;
          }
        }
        this.subscriberReadySbj.next(this.currentSubscriber);
      },
      error => {
        console.log(JSON.stringify(this.domainSvc.getLastErrors()));
      });
  }

  public refreshAccountCache(curAct: string, curActType: IceTypes.ACCOUNT_TYPE, setFormat: boolean = true) {
    if (curAct == null || curAct.length == 0) {
      this.setDateFormat(curAct , curActType);
    }

    this.currentPatient = null;
    if (curActType == IceTypes.ACCOUNT_TYPE.actPatient) {
      this.domainSvc.getPatient(curAct, "").subscribe(
        data => {
          if (data != null && data.length > 0) this.currentPatient = <patient>data[0];

          this.breadCrumbs.FARM.data = data != null && data.length > 0 ? data[0] : null;
          if (this.currentPatient != null) {
            this.breadCrumbs.FARM.display = this.getPatientName(this.currentPatient) + " Chart No. #" + this.currentPatient.trans_account;
          }

          this.patientReadySbj.next(this.currentPatient);
        },
        error => {
          console.log(JSON.stringify(this.domainSvc.getLastErrors()));
        });
    }
    else if (curActType == IceTypes.ACCOUNT_TYPE.actSubscriber) {
      this.domainSvc.getSubscriber(curAct, "").subscribe(
        data => {
          if (data != null && data.length > 0) this.currentSubscriber = <subscriber>data[0];

          this.breadCrumbs.FARM.data = data != null && data.length > 0 ? data[0] : null;
          if (this.currentSubscriber != null) {
            this.breadCrumbs.FARM.display = this.getSubscriberName(this.currentSubscriber) + " Member #" + this.currentSubscriber.subscriber_key;
          }

          this.subscriberReadySbj.next(this.currentSubscriber);
        },
        error => {
          console.log(JSON.stringify(this.domainSvc.getLastErrors()));
        });
    }
    else if (curActType == IceTypes.ACCOUNT_TYPE.actProvider || curActType == IceTypes.ACCOUNT_TYPE.actFacility) {
      this.domainSvc.getProvider(curAct).subscribe(
        data => {
          this.breadCrumbs.FARM.data = null;
          if (data != null && data.length > 0) {
            this.breadCrumbs.FARM.data = data[0];
            let pt_entity = this.domainSvc.getsubtreedata(data[0]["ENTS"], "entity_key", data[0].entity_key);
            if (pt_entity != null) {
              let name = curActType == IceTypes.ACCOUNT_TYPE.actProvider ? pt_entity["prefix"] + " " + pt_entity["first_name"] + " " + pt_entity["last_name"] : pt_entity["organization_name"];
              this.breadCrumbs.FARM.display = name;
              if (!this.getMainAppChildMode())
                this.breadCrumbs.FARM.link = this.navSvc.buildRoute("EHR/Schedule?vw=dayView&date=&todate=", true);
            }
          }
        },
        error => {
          console.log(JSON.stringify(this.domainSvc.getLastErrors()));
        });
    }
    else if (curActType == IceTypes.ACCOUNT_TYPE.actPharmacy) {
      this.domainSvc.getPharmacy(curAct).subscribe(
        data => {
          this.breadCrumbs.FARM.data = null;
          if (data != null && data.length > 0) {
            this.breadCrumbs.FARM.data = data[0];
            let ent = this.domainSvc.getsubtreedata(data[0]["ENTS"], "entity_key", data[0].entity_key);
            if (ent != null) {
              let name = ent["organization_name"];
              this.breadCrumbs.FARM.display = name;
            }
          }
        },
        error => {
          console.log(JSON.stringify(this.domainSvc.getLastErrors()));
        });
    }
    else if (curActType == IceTypes.ACCOUNT_TYPE.actPayer) {
      this.domainSvc.getPayer(curAct).subscribe(
        data => {
          this.breadCrumbs.FARM.data = null;
          if (data != null && data.length > 0) {
            this.breadCrumbs.FARM.data = data[0];
            let ent = this.domainSvc.getsubtreedata(data[0]["ENTS"], "entity_key", data[0].entity_key);
            if (ent != null) {
              let name = ent["organization_name"];
              this.breadCrumbs.FARM.display = name;
            }
          }
        },
        error => {
          console.log(JSON.stringify(this.domainSvc.getLastErrors()));
        });
    }

    if (setFormat) {
      this.setDateFormat(curAct, curActType);
    }
  }

  public setDateFormat(domain: string, accountType: IceTypes.ACCOUNT_TYPE) {
    if (domain == null || domain.length == 0)
      return;

    let dateFormat = this.loginSvc.currentDateFormat;
    try {
      // read date format from options.
      // if no date format, read country code of provider.
      // if no country code, default to mm/dd/yyyy
      let api = "";
      switch (accountType) {
        case IceTypes.ACCOUNT_TYPE.actFacility:
        case IceTypes.ACCOUNT_TYPE.actProvider:
          api = "providers";
          break;
        case IceTypes.ACCOUNT_TYPE.actPharmacy:
          api = "pharmacies";
          break;
        case IceTypes.ACCOUNT_TYPE.actPayer:
          api = "payers";
          break;
        case IceTypes.ACCOUNT_TYPE.actVendor:
          api = "vendors";
          break;
        case IceTypes.ACCOUNT_TYPE.actPatient:
          api = "patients";
          break;
        default:
          this.domainSvc.logMessage(IceTypes.LOGTYPE.ltError, "Error options API not supported. ");
          return;
      }

      this.domainSvc.getOptions(api, domain, accountType).subscribe(
        data => {
          console.log("provider data", data);
          if (data !== null) {
            let dateFormatSet: boolean = false;
            for (let iopt = 0; iopt < data.length; iopt++) {
              let opt = data[iopt];
              if (opt == null)
                continue;

              switch (opt["param_txt"]) {
                case IceTypes.OPTION_KEYS.DATEFORMAT_LOCALITY: {
                  if (dateFormatSet) break;

                  if (Iceutility.isdefined(opt["param_value"])) {
                    dateFormat = opt["param_value"] == "" || opt["param_value"].length == 0
                      ? Iceutility.getDateFormat(IceTypes.DATE_FORMATS.mm_dd_yyyy) : opt["param_value"];
                    sessionStorage.setItem("g_currentdateformat", dateFormat);
                    dateFormatSet = true;
                  }
                  break;
                }
              }
            }
          }

          if (Iceutility.isStringUndefinedNullOrEmpty(dateFormat)) {
            let optFunctionSuccess = (dom) => {
              if (dom == null || dom.length == 0) return;

              let countryCode: string = "";
              if (Iceutility.isdefined(dom) && Iceutility.isdefined(dom[0])) {
                let countries = dom[0]["COUNTRIES"];
                if (Iceutility.isdefined(countries) && Iceutility.isdefined(countries[0])) {
                  countryCode = countries[0].code;
                  dateFormat = this.loginSvc.getLocaleFromCountryCode(countryCode);
                }
              }

              sessionStorage.setItem("g_currentdateformat", dateFormat);
            };

            let optFunctionFailed = (error) => {
              dateFormat = Iceutility.getDateFormat(IceTypes.DATE_FORMATS.mm_dd_yyyy);
            };

            switch (accountType) {
              case IceTypes.ACCOUNT_TYPE.actFacility:
              case IceTypes.ACCOUNT_TYPE.actProvider:
                this.domainSvc.getProvider(domain).subscribe(
                  optFunctionSuccess,
                  optFunctionFailed);
                break;
              case IceTypes.ACCOUNT_TYPE.actPharmacy:
                this.domainSvc.getPharmacy(domain).subscribe(
                  optFunctionSuccess,
                  optFunctionFailed);
                break;
              case IceTypes.ACCOUNT_TYPE.actPayer:
                this.domainSvc.getPayer(domain).subscribe(
                  optFunctionSuccess,
                  optFunctionFailed);
                break;
              case IceTypes.ACCOUNT_TYPE.actVendor:
                this.domainSvc.getVendor(domain).subscribe(
                  optFunctionSuccess,
                  optFunctionFailed);
                break;
              case IceTypes.ACCOUNT_TYPE.actPatient:
                this.domainSvc.getPatient(domain, "").subscribe(
                  optFunctionSuccess,
                  optFunctionFailed);
                break;
              default:
                this.domainSvc.logMessage(IceTypes.LOGTYPE.ltError, "Error options API not supported. ");
                return;
            }
          }
        });
    } catch (e) {
      console.log(e.message);
    }
  }

  getPatient(): patient {
    return this.currentPatient;
  }

  getSubscriber(): subscriber {
    return this.currentSubscriber;
  }

  getPatientName(pt: patient): string {
    let ptname = "";
    let pt_entity = this.domainSvc.getsubtreedata(pt["ENTS"], "entity_key", pt.entity_key);
    if (pt_entity != null) {
      ptname = pt_entity["first_name"] + " " + pt_entity["last_name"];
    }
    return ptname;
  }

  getSubscriberName(sub: subscriber): string {
    let subname = "";
    let sub_entity = this.domainSvc.getsubtreedata(sub["ENTS"], "entity_key", sub.entity_key);
    if (sub_entity != null) {
      subname = sub_entity["first_name"] + " " + sub_entity["last_name"];
    }
    return subname;
  }

  onPatientReady(domain: string): Observable<patient> {    
    if (domain == null || domain.length == 0) return throwError('Invalid domain parameter.');
    return this.patientReadySbj.asObservable().pipe(filter(pt => pt && pt.trans_account.toLowerCase() == domain.toLowerCase()));
  }

  onSubscriberReady(domain: string): Observable<patient> {
    if (domain == null || domain.length == 0) return throwError('Invalid domain parameter.');
    return this.subscriberReadySbj.asObservable().pipe(filter(sub => sub && sub.subscriber_key.toLowerCase() == domain.toLowerCase()));
  }

  addBreadcrumb(display: string, data: any, active: boolean, link: string) {
    this.breadCrumbs.LEAVES.push({ display: display, data: data, active: active, link: link });
  }

  addBreadcrumbToolbar(tag: string) {
    if (tag == null || tag.length == 0) return null;
    if (this.breadCrumbs.TOOLBARS == null || this.breadCrumbs.TOOLBARS.length == 0)
      this.breadCrumbs.TOOLBARS = [];

    let toolBar = {
      tag: tag,
      items: []
    };

    this.breadCrumbs.TOOLBARS.push(toolBar);
    return toolBar;
  }

  setShowPrivateNetworkToolbar(val: boolean): void {
    this.showPrivateNetworkToolbar = val;
  }

  setHideBreadCrum(hide: boolean) {
    this.breadCrumbs.HIDE = hide;
  }

  onDefaultToolbarMenuItemOpen(event) {
    if (event.toolbar && event.toolbarItem) {
      if (event.toolbarItem.menuitem != null && event.toolbarItem.menuitem.length > 0
        && event.toolbar[event.toolbarItem.menuitem].menuItems != null) {
        let menuItems = event.toolbar[event.toolbarItem.menuitem].menuItems;
        let foundRoot = menuItems.filter(x => x.id == "root");
        if (foundRoot.length > 0) {
          foundRoot[0].display = "Open " + event.toolbarItem.display;
          foundRoot[0].link = event.toolbarItem.link;
        }
      }
    }
  }

  onDefaultToolbarMenuItemClick(event, router: Router) {
    if (event.toolbarItem && event.toolbarMenuItem) {
      if (event.toolbarMenuItem.externallink && event.toolbarMenuItem.externallink.length > 0) {
        let tourl: string = this.getNavService().buildRoute(event.toolbarMenuItem.externallink, true);
        location.href = tourl;
      }
      else {
        router.navigate([this.getNavService().buildRoute(event.toolbarMenuItem.link, false)]);
      }
    }
  }

  getPermission(feature): IcecontrolPermission {
    if (!Iceutility.isdefined(feature)) {
      feature = userTypes.USER_FEATURES.ufNone;
    }

    let userInfo = this.loginSvc.GetSessionUserInfo(),
      feaAccess: userTypes.USER_FEATURE_ACCESS = userTypes.USER_FEATURE_ACCESS.ufaNone;

    feaAccess = this.loginSvc.getFeatureAccess(userInfo, this.getCurrentAccount(), feature);

    return new IcecontrolPermission(feature, feaAccess);
  }

  // General Edit Panel Section
  viewEditPanelSection(sectionTag: string, controls: IcecontrolSettings[], submitButtons: boolean = true, submitButtonText: string = "Submit"): IcepanelsectionSettings {
    if (sectionTag == "" || sectionTag == null) throw "Edit Panel section name not defined.";

    let section: IcepanelsectionSettings = null;

    try {
      if (submitButtons == true) {
        controls.push(new IcecontrolSettings("icepanelbutton", { text: submitButtonText, class: [], glyphClass: "fa-plus", width: "115px", section: sectionTag }, null, null, this.getEditPanelSectionSaveID(sectionTag)));
        controls.push(new IcecontrolSettings("icepanelbutton", { text: "Cancel", class: [], glyphClass: "fa-times", width: "115px", section: sectionTag }, null, null, this.getEditPanelSectionCancelID(sectionTag)));
      }
      section = new IcepanelsectionSettings(
        sectionTag,
        { class: [], styles: { width: "100%" } }, [], controls, null)
        .setControlAlignment(SECTION_CONTROL_ALIGNMENT.ctrlAlignHorizontal).setShow(false);

      this.editPanelSectionMap[sectionTag] = section;
    }
    catch (e) {
      console.log(e);
    }
    return section;
  }

  getEditPanelSection(sectionTag: string): IcepanelsectionSettings {
    return this.editPanelSectionMap[sectionTag];
  }

  getEditPanelSectionSaveID(sectionTag: string): string {
    return "IDEdit" + sectionTag + "Save";
  }

  getEditPanelSectionCancelID(sectionTag: string): string {
    return "IDEdit" + sectionTag + "Cancel"
  }

  setEditPanelSectionRow(sectionTag: string, rowData: any): void {
    this.editPanelSectionRowData[sectionTag] = rowData;
  }

  getEditPanelSectionRow(sectionTag: string): any {
    return this.editPanelSectionRowData[sectionTag];
  }

  editPanelSectionUpdate(sectionTag: string, update: boolean, rowData: any): void {
    if (rowData == null) return;

    if (update == false) {
      this.setEditPanelSectionRow(sectionTag, rowData);
      let submitSec = this.getEditPanelSection(sectionTag);
      if (submitSec == null) throw "Error: Unable to initialize the edit panel.";

      for (let colName of Object.keys(rowData)) {
        let colVal = rowData[colName];
        let ctrls = submitSec.controls.filter(c => c.id == colName);
        if (ctrls.length > 0) {
          let ctrl = ctrls[0];
          if (ctrl) {
            if (ctrl.tag == "iceselect") {
              let optList = ctrl.getOptions().filter(opt => colVal == opt.value || colVal == opt.name);
              if (optList.length > 0)
                ctrl.getFormControl().setValue(optList[0].value);
            }
            else if (ctrl.tag == "icedatepicker") {
              if (Iceutility.isDate(colVal)) {
                ctrl.getFormControl().setValue(colVal);
              }
              else {
                let dtVal = Iceutility.isTextDateTime(colVal) ? Iceutility.GetLocalDate(Iceutility.convertToUTCEx(colVal)) : Iceutility.ConvertFromDotNetServerTime(colVal);
                ctrl.getFormControl().setValue(dtVal);
              }
            }
            else if (ctrl.tag == "icelabel") {
              ctrl.getFormControl().setValue(colVal);
            }
            else {
              ctrl.getFormControl().setValue(colVal);
            }
          }
        }
      }
    }
    else {
      let submitSec = this.getEditPanelSection(sectionTag);
      if (submitSec == null) throw "Error: Unable to update data from the edit panel.";

      for (let colName of Object.keys(rowData)) {
        let ctrls = submitSec.controls.filter(c => c.id == colName);
        if (ctrls.length > 0) {
          let ctrl = ctrls[0];
          if (ctrl) {
            if (ctrl.tag == "iceselect") {
              let optList = ctrl.getOptions().filter(opt => ctrl.getFormControl().value == opt.value);
              if (optList.length > 0) {
                rowData[colName] = optList[0].name;
              }
            }
            else if (ctrl.tag == "icedatepicker") {
              rowData[colName] = Iceutility.getDateTextEx(ctrl.getFormControl().value);
            }
            else {
              rowData[colName] = ctrl.getFormControl().value;
            }
          }
        }
      }
      this.setEditPanelSectionRow(sectionTag, null);
    }
  }

  hideEditPanelSection(sectionTag: string): void {
    let submitSec = this.getEditPanelSection(sectionTag);
    if (submitSec) submitSec.setShow(false);
  }

  showEditPanelSection(sectionTag: string, scrollHeight?: number): void {
    let submitSec = this.getEditPanelSection(sectionTag);
    if (submitSec) submitSec.setShow(true);

    if (scrollHeight == null) {
      setTimeout(() => {
        window.scrollTo(0, document.body.scrollHeight);
      }, 500);
    }
    else {
      if (scrollHeight > 0)
        window.scrollTo(0, scrollHeight);
    }

  }

  processEditPanelSectionButtons(sectionTag: string, idToProcess: string, idIsNotInSection?: boolean): boolean {
    let ret = false;
    if (sectionTag == null || sectionTag == "") return ret;
    if (idToProcess == null || idToProcess == "") return ret;

    let submitSec = this.getEditPanelSection(sectionTag);
    if (submitSec != null) {
      let ctrls = submitSec.controls.filter(c => c.id == idToProcess);
      if (ctrls.length > 0 || idIsNotInSection == true) {
        if (this.getEditPanelSectionCancelID(sectionTag) == idToProcess) {
          this.setEditPanelSectionRow(sectionTag, null);
        }
        else {
          this.editPanelSectionUpdate(sectionTag, true, this.getEditPanelSectionRow(sectionTag));
          ret = true;
        }
        this.hideEditPanelSection(sectionTag);
      }
    }
    return ret;
  }

  public setDomainNameList(ctrl: IcecontrolSettings, reset: boolean, results: Observable<any>) {
    if (ctrl == null) return;

    if (reset)
      ctrl.setOptions([], false);

    results.subscribe(
      data => {
        if (data && data.length > 0 && data[0] != null) {
          let opts = data.map(m => { return { value: (m.trans_account == null && m["subscriber_key"] != null) ? m["subscriber_key"] : m.trans_account, name: m.getdisplayname() } });
          ctrl.setOptions(opts, false);
        }
      },
      error => {
        console.log(JSON.stringify(this.domainSvc.getLastErrors()));
      });
  }

  public setStateIDList(medCodeSvc: MedicalcodesService, ctrl: IcecontrolSettings, defCountry: string, addressDataSource?: MatTableDataSource<any>) {
    if (medCodeSvc == null || ctrl == null) return;

    ctrl.setOptions([]);
    debugger;
    medCodeSvc.getCodes("STATE", "", defCountry, "").subscribe(
      data => {
        if (data && data.length > 0 && data[0] != null) {
          let opts = data.map(c => { return { value: c.code, name: c.title + " (" + c.code + ")" } });
          opts.sort(function (a, b) { return a.name.localeCompare(b.name) });
          ctrl.setOptions(ctrl.getOptions().concat(opts));
          this.stateOptions = ctrl.getOptions(); // save options

          if (addressDataSource != null) {
            for (let iAd = 0; iAd < addressDataSource.data.length; iAd++) {
              let adObj = addressDataSource.data[iAd];
              if (adObj == null) continue;

              let optList = ctrl.getOptions().filter(opt => adObj["state_id"] == opt.value);
              if (optList.length > 0) {
                addressDataSource.data[iAd]["state_id"] = optList[0].name;
              }
            }
          }
        }
      },
      error => {
        console.log(JSON.stringify(medCodeSvc.getLastErrors()));
      });
  }

  public setCountryCodeList(medCodeSvc: MedicalcodesService, ctrl: IcecontrolSettings, addressDataSource?: MatTableDataSource<any>) {
    if (medCodeSvc == null || ctrl == null) return;

    ctrl.setOptions([]);
    medCodeSvc.getCodes("COUNTRY", "", "", "").subscribe(
      data => {
        if (data && data.length > 0 && data[0] != null) {
          let opts = data.map(c => { return { value: c.code, name: c.title + " (" + c.code + ")" } });
          opts.sort(function (a, b) { return a.name.localeCompare(b.name) });
          ctrl.setOptions(ctrl.getOptions().concat(opts));
          this.countryOptions = ctrl.getOptions(); // save options

          if (addressDataSource != null) {
            for (let iAd = 0; iAd < addressDataSource.data.length; iAd++) {
              let adObj = addressDataSource.data[iAd];
              if (adObj == null) continue;

              let optList = ctrl.getOptions().filter(opt => adObj["country_id"] == opt.value);
              if (optList.length > 0) {
                addressDataSource.data[iAd]["country_id"] = optList[0].name;
              }
            }
          }
        }
      },
      error => {
        console.log(JSON.stringify(medCodeSvc.getLastErrors()));
      });
  }

  public setTaxonomyCodeList(medCodeSvc: MedicalcodesService, ctrl: IcecontrolSettings, codes: string[], callback?: Function) {
    if (medCodeSvc == null || ctrl == null) return;

    ctrl.setOptions([]);
    medCodeSvc.getCodes("TAXONOMY", "", codes.join(','), "").subscribe(
      data => {
        if (data && data.length > 0 && data[0] != null) {
          let opts = data.map(c => { return { value: c.code, name: c.description + " (" + c.code + ")" } });
          opts.sort(function (a, b) { return a.name.localeCompare(b.name) });
          ctrl.setOptions(ctrl.getOptions().concat(opts));

          if (callback == null) {
            let optList = opts.filter(opt => ctrl.getFormControl().value == opt.value);
            if (optList.length > 0) {
              ctrl.getFormControl().setValue(optList[0].name);
            }
          }
          else {
            callback();
          }
        }
      },
      error => {
        console.log(JSON.stringify(medCodeSvc.getLastErrors()));
      });
  }

  public setCodeList(medCodeSvc: MedicalcodesService, obj: basecode, ctrl: IcecontrolSettings, codes: string[], reset?: boolean, keyword?: string) {
    if (medCodeSvc == null || obj == null || ctrl == null) return;

    if (reset == null || reset == true)
      ctrl.setOptions([]);

    medCodeSvc.getCodes(obj.gettablename(), "", codes.join(','), keyword).subscribe(
      data => {
        if (data && data.length > 0 && data[0] != null) {
          let ditCodes = [];
          let opts = [];

          if (obj.gettablename() == "SUBTYPE_ITEM") {
            opts = data.map(c => {
              return { value: Iceutility.toLong(c.sub_type), name: c.title + " (" + c.code + ")" }
            });
          }
          else {
            opts = data.map(c => {
              if (c.DITXML != null && c.DITXML.length > 0) ditCodes.push(c);
              return { value: c.code, name: c.title + " (" + c.code + ")" }
            });
          }

          let res = MedicalcodesService.searchCodeByKeyword(ditCodes, keyword, { resultCodeField: "value", resultTitleField: "name", appendfields: ['*'] })
          if (res != null && res.length > 0) {
            opts = opts.concat(res);
          }
          ctrl.setOptions(ctrl.getOptions().concat(opts), false);
          let newSet = ctrl.getOptions();
          newSet = newSet.filter((v, i, a) => a.findIndex(t => (t.value === v.value)) === i);
          newSet.sort(function (a, b) { return a.name.localeCompare(b.name) });

          if (ctrl.tag == "iceautocomplete") {
            let val = ctrl.getFormControl().value;
            if (val != null && val.length > 0) {
              try {
                let valObj = JSON.parse(val); // this should be an array
                let optList = opts.filter(opt => valObj.value == opt.value);
                if (optList.length == 0) {
                  ctrl.setOptions(ctrl.getOptions().concat(valObj), false);
                }
              }
              catch (e) {
                console.log(e);
              }
            }
          }
          else {
            let optList = opts.filter(opt => ctrl.getFormControl().value == opt.value);
            if (optList.length > 0)
              ctrl.getFormControl().setValue(optList[0].name);
          }
       }
      },
      error => {
        console.log(JSON.stringify(medCodeSvc.getLastErrors()));
      });
  }

  public GetNoteStatusTypeValue(statusType) {
    if (!Iceutility.isdefined(statusType)) {
      return "";
    }
    switch (Iceutility.toLong(statusType)) {
      case IceTypes.notestatus.statusInitialDraft: { return IceTypes.NoteStatus.statusInitialDraft.name; }
      case IceTypes.notestatus.statusInProgress: { return IceTypes.NoteStatus.statusInProgress.name; }
      case IceTypes.notestatus.statusComplete: { return IceTypes.NoteStatus.statusComplete.name; }
      case IceTypes.notestatus.statusCompleteRequired: { return IceTypes.NoteStatus.statusCompleteRequired.name; }
      case IceTypes.notestatus.statusPatientCompleteRequired: { return IceTypes.NoteStatus.statusPatientCompleteRequired.name; }
    }
    return "";
  }

  valueToExternalTypeText(value) {
  if (!Iceutility.isdefined(value))
    return "";

  try {
    switch (value) {
      case IceTypes.EXTERNAL_DATA_TYPE_MAP.edtypeText.value:
      default:
        return "text/plain";
      case IceTypes.EXTERNAL_DATA_TYPE.edtypeJpeg:
        return "image/jpeg";
      case IceTypes.EXTERNAL_DATA_TYPE.edtypeGIF:
        return "image/gif";
      case IceTypes.EXTERNAL_DATA_TYPE.edtypePNG:
        return "image/png";
      case IceTypes.EXTERNAL_DATA_TYPE.edtypeTiff:
        return "image/tiff";
      case IceTypes.EXTERNAL_DATA_TYPE.edtypeBitmap:
        return "image/bmp";
      case IceTypes.EXTERNAL_DATA_TYPE.edtypePDF:
        return "application/pdf";
      case IceTypes.EXTERNAL_DATA_TYPE.edtypeMsword:
        return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      case IceTypes.EXTERNAL_DATA_TYPE.edtypeRAR:
        return "application/x-rar-compressed";
      case IceTypes.EXTERNAL_DATA_TYPE.edtypeMsexcel:
        return "application/vnd.ms-excel";
      case IceTypes.EXTERNAL_DATA_TYPE.edtypePowerpoint:
        return "application/vnd.ms-powerpoint";
      case IceTypes.EXTERNAL_DATA_TYPE.edtypeICEMEDX:
        return "application/icex";
      case IceTypes.EXTERNAL_DATA_TYPE.edtypeMpeg:
        return "video/mpeg";
      case IceTypes.EXTERNAL_DATA_TYPE.edtypeMp4:
        return "video/mp4";
      case IceTypes.EXTERNAL_DATA_TYPE.edtypeWebm:
        return "video/webm";
      case IceTypes.EXTERNAL_DATA_TYPE.edtypeWmv:
        return "video/x-ms-wmv";
      case IceTypes.EXTERNAL_DATA_TYPE.edtypeWma:
        return "audio/x-ms-wma";
    }
  } catch (e) {
    console.log(e.message);
  }
}
}

export class DomainAppService extends BaseAppService {

  customBrandName: string = "";

  constructor(navSvc: IceNavService, domainSvc: DomainService, loginSvc: LoginService, app: ApplicationRef) {
    super(navSvc, domainSvc, loginSvc, app);

    this.setCustomBrandName(null);
  }

  public defaultMenus() {
    super.defaultMenus();
  }
}

